<template>
  <div id="page">
    <section class="container-fluid breadcrumbs d-none d-md-block">
      <div class="container">
        <div class="row">
          <div class="col-12">
            <nav aria-label="breadcrumb">
              <ol class="breadcrumb">
                <li class="breadcrumb-item"><a href="#">Home</a></li>
                <li class="breadcrumb-item active" aria-current="page">{{ $t('views.login.breadcrumbpage') }}</li>
              </ol>
            </nav>
          </div>
        </div>
      </div>
    </section>

    <main id="main" class="">
      <div class="container px-xs-0 beratung">
        <div class="row">
          <div class="login-heading">
            <i aria-hidden="true" class="icon icon--lock-solid"></i>
            <div>
              <h1>{{ $t('views.login.breadcrumbpage') }}</h1>
              <small class="">{{ $t('views.appointment.loggedinas') }} {{ user }} <a class="ms-1" href="#" @click="logout">{{ $t('views.appointment.logout') }}</a></small>
            </div>
          </div>
        </div>
        <div class="login-content logged-in">
          <ul class="login-content--subnav">
            <li class="login-content--subnav--item" :class="{ active: isRouteActive('widget-appointment') }"
              @click="navigateToAppointments">
              <i aria-hidden="true" class="icon icon--calendar"></i>
              {{ $t('views.appointment.termine') }}
            </li>
            <li class="login-content--subnav--item" :class="{ active: isRouteActive('widget-account') }"
              @click="navigateToAccount">
              <i aria-hidden="true" class="icon icon--user"></i>
              {{ $t('views.appointment.account') }}
            </li>
          </ul>
          <div class="login-content--main flow">
            <h2 class="h1">{{ $t('views.appointment.account') }}</h2>
            <p class="text-muted">{{ $t('views.account.edit') }}</p>
            <p>
              {{ $t('views.account.editinfo') }}
            </p>
            <p>
              {{ $t('views.account.editinfo1') }}
            </p>
            <div v-if="error != ''" class="form-text alert alert-danger">
              {{ error }}
            </div>

            <div class="row">

                <div class="col-12 col-lg-4 mb-5 mb-lg-0">
                  <div class="mb-4">
                    <label class="form-label" for="mail">{{ $t('views.register.email') }}</label>
                    <input type="text" class="form-control" v-model="email" id="mail" autocomplete="off"
                      :name="generateRandomName('email')" @focus="mailHasFocus = true"  @blur="mailHasFocus = false">
                    <div v-if="email != '' && !mailHasFocus && !isValidEmail(email)" class="form-text alert alert-danger">{{ $t('views.register.emailinvalid') }}</div>
                  </div>
                  <div class="mb-4">
                    <label class="form-label" for="phone">{{ $t('views.register.phone') }}</label>
                    <input type="text" class="form-control" v-model="phone" id="phone" autocomplete="off"
                      :name="generateRandomName('phone')">
                    <div v-if="phone != '' && !isValidPhone(phone)" class="form-text alert alert-danger">{{ $t('views.register.phoneinvalid') }}</div>
                  </div>

                  <p class="text-muted">{{ $t('views.account.changepw') }}</p>

                  <div class="mb-4">
                    <label class="form-label" for="password">{{ $t('views.account.oldpw') }}</label>
                    <div class="pw-input--wrapper">
                      <input :type="oldPasswordFieldType" class="form-control" v-model="oldPassword" id="oldPassword"
                        :name="generateRandomName('oldPassword')" autocomplete="off" aria-describedby="passwordHelp"
                      >
                      <i data-target="oldPassword"
                        :class="oldPasswordFieldType === 'password' ? 'onb-visible--on' : 'onb-visible--off'"
                        class="icon pw-input--switch" @click="toggleOldPassword"
                        :title="oldPasswordFieldType === 'password' ? $t('views.login.showpw') : $t('views.login.hidepw')"
                        :aria-label="oldPasswordFieldType === 'password' ? $t('views.login.showpw') : $t('views.login.hidepw')"></i>
                    </div>
                  </div>
                  <div class="mb-4">
                    <label class="form-label" for="password">{{ $t('views.account.newpw') }}</label>
                    <div class="pw-input--wrapper">
                      <input :type="passwordFieldType" class="form-control" v-model="password" id="password"
                        :name="generateRandomName('newPassword')" autocomplete="off" aria-describedby="passwordHelp"
                      >
                      <i data-target="password"
                        :class="passwordFieldType === 'password' ? 'onb-visible--on' : 'onb-visible--off'"
                        class="icon pw-input--switch" @click="togglePassword"
                        :title="passwordFieldType === 'password' ? $t('views.login.showpw') : $t('views.login.hidepw')"
                        :aria-label="passwordFieldType === 'password' ? $t('views.login.showpw') : $t('views.login.hidepw')"></i>
                    </div>
                  </div>

                  <div v-if="showPasswordHelp" id="passwordHelp" class="form-text alert alert-danger">
                    {{ $t('views.register.pwhint') }}
                  </div>

                  <button class="btn btn-primary-custom mb-4" @click="update">{{ $t('common.save') }}</button>

                  <div v-if="passwordError" class="form-text alert alert-danger">
                    {{ $t('common.passwordWarning') }}
                  </div>

                  <div v-if="success != ''" class="form-text alert alert-success">
                    {{ success }}
                  </div>

                  <h4 class="my-3">{{ $t('views.account.deleteinfo') }}</h4>
                  <button class="btn btn-primary-custom" @click="remove">{{ $t('views.account.delete') }}</button>

                </div>

            </div>

          </div>
        </div>

      </div> <!-- /container -->
    </main>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import {
  AppointmentDto,
  CounselingCenterDetailsDto, CounselingCenterSearchResultDto, FacetResultCounselingCenterSearchResultDto,
  LegalEntityDto,
  LookupValueDto,
  RegionDto,
  ResultAppointmentDto, ResultString
} from "@/generated/openapi";
import appointmentService from "@/services/appointmentService";
import config from "@/config/config";
import {
  APPOINTMENTS_SORT_VALUES,
  COUNSELING_CENTERS_SORT_VALUES,
  createDefaultTableSortOptions
} from "@/models/OnbTableColumnDefinition";
import LoadingMixin from "@/mixins/LoadingMixin";
import counselingCenterService from "@/services/counselingCenterService";
import AuthService from "@/services/authService";
import { asSortString } from "@/utils/apiUtils";
import { mapActions } from "vuex";
import { SelectOption } from "@/models/SelectOption";
import { useRouter } from 'vue-router';
import { isValidEmail, isTelephoneString } from '@/utils/validationUtils';

export default defineComponent({
  name: 'AccountView',
  components: {
  },
  mixins: [
    LoadingMixin
  ],
  data() {
    const router = useRouter();

    return {
      error: '',
      success: '',
      username: '',
      password: '',
      oldPassword: '',
      oldPasswordFieldType: 'password',
      passwordFieldType: 'password',
      passwordError: '',
      acceptTerms: '',
      email: '',
      phone: '',
      showPasswordHelp: false,
      appointments: {} as ResultAppointmentDto,
      counselingCenter: {} as CounselingCenterDetailsDto,
      counselingCenters: {} as FacetResultCounselingCenterSearchResultDto,
      legalEntity: {} as LegalEntityDto,
      advisors: {} as ResultString,
      offset: 0,
      router,
      sortOptions: createDefaultTableSortOptions(COUNSELING_CENTERS_SORT_VALUES),
      appointmentsSortOptions: createDefaultTableSortOptions(APPOINTMENTS_SORT_VALUES),
      searchMessages: Object,
      selectedRegion: String,
      selectedLong: String,
      selectedLat: String,
      selectedTopics: [] as any,
      onlineberatung: Boolean,
      availableRegions: [
        { label: this.$t("common.region.ALL"), value: "all" } as SelectOption,
        { label: this.$t("common.region.BURGENLAND"), value: 'BURGENLAND' } as SelectOption,
        { label: this.$t("common.region.CARINTHIA"), value: 'CARINTHIA' } as SelectOption,
        { label: this.$t("common.region.LOWER_AUSTRIA"), value: 'LOWER_AUSTRIA' } as SelectOption,
        { label: this.$t("common.region.UPPER_AUSTRIA"), value: 'UPPER_AUSTRIA' } as SelectOption,
        { label: this.$t("common.region.SALZBURG"), value: 'SALZBURG' } as SelectOption,
        { label: this.$t("common.region.STYRIA"), value: 'STYRIA' } as SelectOption,
        { label: this.$t("common.region.TYROL"), value: 'TYROL' } as SelectOption,
        { label: this.$t("common.region.VORARLBERG"), value: 'VORARLBERG' } as SelectOption,
        { label: this.$t("common.region.VIENNA"), value: 'VIENNA' } as SelectOption
      ] as SelectOption[],
      type: "home",
      mailHasFocus: false
    }
  },
  mounted() {
    this.findAppointments();
    this.findCounselingCenters();

    if (this.messages) {
      this.searchMessages = Object.assign({}, JSON.parse(this.messages))
    }

    this.getAllLookUpValuesTopic().then(_ => {
      this.getAllRegions().then(_ => {
      }).catch(_ => {
      })
      // this.state = LoadingState.success
    }).catch(_ => {
    })

    let email = sessionStorage.getItem("email");
    let phone = sessionStorage.getItem("phone");

    this.email = (email === null) ? '' : email;
    this.phone = (phone === null) ? '' : phone;

  },
  computed: {
    filteredAppointments() {
      return this.appointments.content;
    },
    user() {
      return sessionStorage.getItem('user');
    },
    lookUpValuesTopic(): Array<LookupValueDto> {
      if (this.$store.state.allLookUpValuesTopic) {
        return this.$store.state.allLookUpValuesTopic.get('topic')
      }
      return []
    },
    regions(): Array<RegionDto> {
      if (this.$store.state.regions) {
        return this.$store.state.regions
      }
      return []
    }
  },
  methods: {
    ...mapActions(['getAllLookUpValuesTopic', 'getAllRegions']),
    isRouteActive(basePath: string) {
      return this.$route.name?.toString().startsWith(basePath);
    },
    generateRandomName(base: string) {
      return base + Math.random().toString(36).substring(2, 15);
    },
    getEmit(...value: any) {
      this.$emit('submit', value[0], value[1], value[2], undefined, value[3]);
    },
    toggleOldPassword() {
      this.oldPasswordFieldType = this.oldPasswordFieldType === 'password' ? 'text' : 'password';
    },
    togglePassword() {
      this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
    },
    async logout() {
      await AuthService.logout().then(() => {
        this.navigateToAccount();
      });
    },
    navigateToAppointments() {
      this.$router.push('/')
    },
    navigateToAccount() {
      this.$router.push('/account')
    },
    isValidEmail(mail: string) {
      return isValidEmail(mail);
    },
    isValidPhone(phone: string) {
      return isTelephoneString(phone);
    },
    findCounselingCenters(resetOffset = false) {
      const sort = asSortString(this.sortOptions);
      counselingCenterService.getCounselingCenters(undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined,undefined, 10000, 0, sort).then(result => {
        this.counselingCenters = result
      }).catch(() => {
      })
    },
    getCounselingCenterName(id: string) {
      const counselingCenter = this.counselingCenters.content?.find((cc: CounselingCenterSearchResultDto) => cc.id === id);
      return counselingCenter ? counselingCenter.name : '';
    },
    findUsers(resetOffset = false) {
      appointmentService.getAdvisors().then(result => {
        this.advisors = result;
      }).catch(() => {
      })
    },
    findAppointments(resetOffset = false) {
      const sortString = asSortString(this.appointmentsSortOptions);
      appointmentService.getAppointmentsPublic(config.pagingSize, this.offset, sortString).then(result => {

        // add api filter (time_until)
        const filteredAppointments = result.content?.filter((r: AppointmentDto) => this.checkInTime(r.endTime)) as Array<AppointmentDto>;

        this.appointments = {
          content: filteredAppointments,
          totalCount: filteredAppointments?.length
        } as ResultAppointmentDto;

      }).catch(() => {
      })
    },
    getCounselingCenter(id: string) {
      counselingCenterService.getCounselingCenter(id).then(cc => {
        this.counselingCenter = cc;
      }).catch(err => {

      });
    },
    checkInTime(d: string): boolean {
      const currentDate = new Date();
      const inputDate = new Date(d);
      return inputDate > currentDate;
    },
    async update() {
      if ((this.oldPassword || this.password) && (!this.checkPassword(this.oldPassword) || !this.checkPassword(this.password))) {
        this.passwordError = this.$t('common.passwordWarning');
        return;
      }
      //return;
      this.passwordError = '';
      const username = this.user || '';
      try {
        await AuthService.update(username, this.email, this.phone, this.oldPassword, this.password);
        sessionStorage.setItem('phone', this.phone);
        sessionStorage.setItem('email', this.email);
        this.success = this.$t('common.changeSuccessful');
      } catch (err: any) {
        if (err && err.message == "client__invalid_phone") {
          this.error = this.$t('common.phoneChangeWarning');
        } else {
          this.error = this.$t('common.passwordChangeWarning');
        }
      }

    },
    async remove() {
      try {
        const username = this.user || '';
        await AuthService.remove(username);
        await AuthService.logout();

        this.success = this.$t('common.deleteSuccessful');

        setTimeout(() => {
          this.navigateToLogin();
        }, 3000);

      } catch (err: any) {
        if (err && err.message == "client__has_active_appointments") {
          this.error = this.$t('common.userDeletionFailedDueActiveAppointments');
        } else {
          this.error = this.$t('common.userDeletionFailed');
        }
      }
    },
    checkPassword(p: string) {
      const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=(?:[^0-9]*[0-9]){2}).{10,}$/;

      if (passwordRegex.test(p)) {
        this.showPasswordHelp = false;
        return true;
      } else {
        this.showPasswordHelp = true;
        return false;
      }
    },
    navigateToLogin() {
      this.router.push('/');
    },
  }
});
</script>
